import React, {
  useContext,
  useState,
  useRef,
  useEffect,
  Fragment,
  useCallback,
} from 'react';
import styled from 'styled-components';
import {message} from 'antd';
import {Context} from '../AppContext';
import {SelectRow} from './ProductParam';
import ProductConfig from './ProductConfig';
import {Close} from '@styled-icons/material';
import {AlertCircle} from '@styled-icons/feather';
import {Text, Button, Row, Label, TextArea, Popover} from '../Widgets';
import {sliceFilename} from '../Utils';
import {REVIEW_STATUS, PRODUCTION_STATUS} from '../dictionary';
import Item, {Col} from '../Templates/Cart/Item';
import useDimension from '../hooks/use-dimension';
import {getCalculation} from '../Utils/OrderUtil';
import useCartItemTable from '../hooks/use-cart-item-table';
import UploadButton from './UploadFileButton';
import Tag from '../Widgets/Tag';
const config = require('../data.json');
const slugify = require('uslug');

export default function CartItemTable(props) {
  const {
    viewOnly,
    order, //only for order detail
    orderItems, //only for order detail
    onUpdate, //only for order detail
  } = props;
  const {spec, cart, removeItem, onConfigChange} = useCartItemTable({order});

  const columns = useRef([
    {
      name: 'index',
      label: '項目',
      render: ({index}) => `${index + 1}`.padStart(2, '0'),
      className: '',
      style: {flex: '1 0 80px'},
    },
    {
      name: 'name',
      label: '商品名稱',
      render: ({item, product}) => {
        return (
          <div>
            <div style={{display: 'flex'}}>
              <div
                onClick={() => {
                  if (
                    typeof window !== 'undefined' &&
                    order?.order_type !== 'custom'
                  ) {
                    window.open(`/product/${slugify(item.name)}/`, '_blank');
                  }
                }}
                style={{
                  color: config.colors.main,
                  textDecoration: 'baseline',
                  cursor: 'pointer',
                  marginRight: 5,
                }}>
                {(order && order.custom_note) || item.name}
              </div>
              {product?.free_shipping && (
                <Tag
                  color={config.colors.main}
                  text="免運"
                  style={{marginRight: 4}}
                />
              )}
              {product?.is_period && (
                <Tag
                  color={config.colors.main}
                  text="訂閱"
                  style={{marginRight: 4}}
                />
              )}
            </div>

            {item.image && (
              <img
                src={item.image}
                alt="product"
                style={{
                  display: 'block',
                  marginTop: 15,
                  width: 270,
                  height: 270,
                  objectFit: 'cover',
                }}
              />
            )}
          </div>
        );
      },
      className: 'product-name',
      style: {flex: '2 0 300px'},
    },
    {
      name: 'spec',
      label: '規格',
      render: ({item, index, product}) => (
        <div>
          <ProductConfig
            product={product}
            config={item.config}
            onChange={(nextConfig) =>
              onConfigChange(index, nextConfig, product)
            }
            viewOnly={viewOnly}
          />
          {order?.order_type === 'custom' && (
            <SelectRow>
              <label style={{paddingTop: 0}}>自定規格</label>
              <Text size="sm" inline>
                {item.custom_note}
              </Text>
            </SelectRow>
          )}
          {item.require_day !== 0 && item.require_day && (
            <SelectRow style={{alignItems: 'baseline'}}>
              <label style={{paddingTop: 0}}>製作時間</label>
              <div className="select-options">
                <Text
                  size="lg"
                  color={config.colors.main}
                  inline
                  style={{marginRight: 5}}>{`${item.require_day}`}</Text>
                <Text size="xs" inline>
                  天（審稿成功後隔日起算工作天，工作天不含假日及運送時間）
                </Text>
              </div>
            </SelectRow>
          )}
        </div>
      ),
      className: '',
      style: {flex: '2 0 300px'},
    },
    {
      name: 'attatchment',
      label: '上傳商品檔案',
      render: ({item, orderItem, disabled}) => {
        return (
          orderItem &&
          (orderItem.file_upload || order?.order_type === 'custom') && (
            <div>
              {!orderItem.latest_attachment && (
                <Warning message="商品在上傳檔後才會進審稿流程" />
              )}

              {!disabled && (
                <div>
                  <UploadButton onUpdate={onUpdate} orderItem={orderItem} />
                  <Popover
                    label={'上傳檔案條件說明'}
                    content={
                      <ol style={{padding: 10}}>
                        <li>
                          如需上傳多個檔案請將文件壓縮為 Rar/Zip 格式上傳。
                        </li>
                        <li>送印檔案支援 Adobe Illustrator CS6 檔。</li>
                        <li>
                          請勿將印製圖檔置入PPT/Work檔/Excel等
                          文字編輯檔格式中上傳提供。
                        </li>
                        <li>請勿傳送雲端連結。</li>
                      </ol>
                    }
                  />
                </div>
              )}
              <Row>
                <Label size="sm" flex="0 0 auto">
                  稿件名稱：
                </Label>
                {orderItem.latest_attachment && (
                  <a
                    style={{
                      color: config.colors.main,
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      lineBreak: 'anywhere',
                    }}
                    href={orderItem.latest_attachment.file}
                    target="_blank"
                    rel="noreferrer">
                    {sliceFilename(orderItem.latest_attachment.file)}
                  </a>
                )}
              </Row>
            </div>
          )
        );
      },
      className: '',
      style: {flex: '1 0 170px'},
    },
    {
      name: 'status',
      label: '檔案備註及狀態',
      render: ({item, orderItem, disabled}) => {
        return orderItem ? (
          <div style={{width: '100%'}}>
            <Label size="sm" flex="0 0 auto">
              檔案製作備註 ：
            </Label>
            <ProductionNote orderItem={orderItem} disabled={disabled} />

            {(orderItem.file_upload || order?.order_type === 'custom') && (
              <Fragment>
                <Row margin="0 0 5px 0">
                  <Label size="sm" flex="0 0 auto">
                    審稿狀態：
                  </Label>
                  <Text
                    size="sm"
                    color={
                      orderItem.status === 'rejected' ? config.colors.error : ''
                    }>
                    {REVIEW_STATUS[orderItem.status]}
                  </Text>
                </Row>

                {orderItem.status === 'rejected' && (
                  <Row margin="0 0 5px 0">
                    <Label size="sm" flex="0 0 auto">
                      不合格原因：
                    </Label>
                    <Text size="sm" color={config.colors.error}>
                      {orderItem.reject_reason ||
                        '無填寫不合格原因，請洽詢客服'}
                    </Text>
                  </Row>
                )}
                <Row margin="0 0 5px 0">
                  <Label size="sm" flex="0 0 auto">
                    印製狀態：
                  </Label>
                  <Text
                    size="sm"
                    color={
                      orderItem.status !== 'waiting'
                        ? config.colors.main
                        : orderItem.status === 'rejected'
                        ? config.colors.error
                        : ''
                    }>
                    {PRODUCTION_STATUS[orderItem.production_status]}
                  </Text>
                </Row>
                <Row margin="0 0 5px 0" style={{lineBreak: 'anywhere'}}>
                  <Label size="sm" flex="0 0 auto">
                    審稿人員備註：
                  </Label>
                  <Text
                    size="sm"
                    color={
                      orderItem.status === 'rejected' ? config.colors.error : ''
                    }
                    inline>
                    {orderItem.note || '無'}
                  </Text>
                </Row>
              </Fragment>
            )}
            <Row margin="0 0 5px 0">
              <Text size="sm" color="#999">
                稿件審稿狀態會由系統發送E-mail通知，請務必自行到信箱或是官網確認狀態，恕不另行電話通知，謝謝。
              </Text>
            </Row>
          </div>
        ) : null;
      },
      className: '',
      style: {flex: '1 0 250px'},
    },
    {
      name: 'price',
      label: '價格（含稅）',
      render: ({item}) => (
        <span style={{fontWeight: '600'}}>
          ${' '}
          {item.amount === item.price
            ? (item.amount * 1.05).toFixed(0)
            : item.price}
        </span>
      ),
      className: '',
      style: {flex: '1 0 150px'},
    },
    {
      name: 'delete',
      label: '' /* 刪除保留欄位 */,
      render: ({index}) => (
        <DeleteButton onClick={() => removeItem(index)}>
          <Close size={18} color="ddd" />
        </DeleteButton>
      ),
      className: '',
      style: {flex: '1 0 30px'},
    },
  ]).current;
  /*
  order && orderItem >  attatchment and status
  !viewOnly > delete
  */

  const getColumns = () => {
    return columns.filter((col) => {
      if (viewOnly && col.name === 'delete') {
        return false;
      }
      if (
        (!order || !orderItems) &&
        ['attatchment', 'status'].includes(col.name)
      ) {
        return false;
      }

      return true;
    });
  };

  return (
    <Table>
      <Header columns={getColumns()} viewOnly={viewOnly} order={order} />
      {cart && spec ? (
        cart.items.map((item, idx) => (
          <Item
            key={item.key}
            index={idx}
            item={item}
            spec={spec}
            columns={getColumns()}
            order={order}
            orderItems={orderItems}
          />
        ))
      ) : (
        <Text style={{textAlign: 'center'}}>載入商品規格中...</Text>
      )}
      {cart && (
        <Footer count={cart.items.length} calculations={cart.calculations} />
      )}
    </Table>
  );
}

const Table = styled.div`
  margin-bottom: 15px;
  border: 1px solid ${config.colors.border};
  border-radius: 4px;
`;

export function Header(props) {
  let {viewOnly, order, columns} = props;

  const {dimension} = useDimension();
  const isSmallScreen = dimension.outerWidth <= config.breakpoints.lg;

  if (isSmallScreen) {
    return (
      <Block style={{borderBottom: `solid 1px ${config.colors.border}`}}>
        <Col>商品名稱</Col>
      </Block>
    );
  }
  return (
    <Block style={{borderBottom: `solid 1px ${config.colors.border}`}}>
      {columns.map((col, idx) => (
        <Col key={idx} style={col.style} className={col.className}>
          {col.label}
        </Col>
      ))}
    </Block>
  );
}

function Footer({count, calculations}) {
  const value = `$ ${
    calculations ? calculations.items_amount + calculations.items_tax : '-'
  }`;

  return (
    <Block
      style={{borderTop: `solid 1px ${config.colors.border}`, padding: 15}}>
      <Col flex="3">
        <Text size="sm">總計 {count} 件商品</Text>
      </Col>
      <Col flex="0 0 250px" style={{padding: 0}}>
        <div style={{display: 'flex', alignItems: 'flex-end'}}>
          <Text weight="500" size="sm" style={{marginRight: 10}}>
            商品價格合計(含稅)
          </Text>
          <Text
            weight="500"
            size="xl"
            color={config.colors.main}
            style={{lineHeight: 1.1}}>
            {value}
          </Text>
        </div>
        <Text size="xs" color={config.colors.main}>
          ＊該銷售額未含運費及折扣金額。
        </Text>
      </Col>
    </Block>
  );
}

function Warning({message}) {
  return (
    <div
      style={{
        padding: 10,
        marginBottom: 10,
        maxWidth: 200,
        backgroundColor: `${config.colors.warning}39`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}>
      <AlertCircle color={config.colors.warning} size="25" />
      <Text
        size="sm"
        color={config.colors.warning}
        style={{marginTop: 5, textAlign: 'center'}}>
        {message}
      </Text>
    </div>
  );
}

function ProductionNote(props) {
  const [note, setNote] = useState('');
  const {orderItem, disabled} = props;
  const app = useContext(Context);

  useEffect(() => {
    if (orderItem) {
      setNote(orderItem.note_2 || orderItem.latest_attachment?.note || '');
    } // note_2 for production_note, due to non-attachment item
  }, [orderItem]);

  const submit = useCallback(async () => {
    app.actions.setLoading(true);

    try {
      let result = await app.actions.editOrderItem({
        id: orderItem.id,
        note_2: note, /// note_2 for production_note, due to non-attachment item
      });
      setNote(result.note_2);
      message.success('儲存成功');
    } catch (ex) {
      console.warn(ex);
      message.error('修改錯誤');
    }
    app.actions.setLoading(false);
  }, [app.actions, orderItem.id, note]);

  return (
    <>
      <TextArea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        disabled={disabled}
      />
      <Button
        style={{
          margin: '5px 0',
        }}
        onClick={submit}
        disabled={disabled}
        block>
        儲存製作備註{' '}
      </Button>
    </>
  );
}

const Block = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  background-color: #fafafa;
  flex: ${(props) => props.flex || 1};
  padding: 10px 15px;
  ${(props) => props.css}
`;

const DeleteButton = styled.div`
  border: solid 1px ${config.colors.border};
  border-radius: 2px;
  padding: 0 3px;
  width: 26px;
  height: 26px;
  cursor: pointer;

  &:hover {
    opacity: 0.75;
  }

  @media only screen and (max-width: ${config.breakpoints.lg}px) {
    position: absolute;
    top: 10px;
    right: 15px;
  }
`;
